'use client';
import { usePathname } from 'next/navigation';
import { Disclosure } from '@headlessui/react';
import { HiBars3, HiXMark } from 'react-icons/hi2';
import { PageType } from '@/app/[locale]/admin/pages/pageType';
import React from 'react';
import { ImageInfo, Site } from '@/components/data/dataType';
import SignOut from '@/components/SignOut';
import NextImage from '@/components/Image/NextImage';
import Link from 'next/link';
import { DictionaryItem } from '@/app/[locale]/dictionaries';

interface PageLink {
    title: string;
    link: string;
}

const Navbar: React.FC<{
    locale: string;
    pages: PageType[];
    site: Site;
    translations: { [key: string]: DictionaryItem };
}> = ({ locale, pages, site }) => {
    const pathName = usePathname();
    const segments = pathName.split('/');
    if (segments.length) {
        if (segments[0] === '') {
            segments.shift();
        }
    }
    let segment = '';

    if (segments.length) {
        segment = segments[0];
    }

    const pageLinks = pages
        .filter(f => f.navbar === true)
        .map(page => ({
            title: page.linkTitle[locale],
            link: page.slug[locale],
        }));

    const imageInfo: ImageInfo = site.logo;
    const batterieLogo: ImageInfo = {
        bucket: 'locationdesjardins',
        folder: 'divers',
        itemId: 1,
        name: 'logo-entete-batteries-expert.png',
        width: 240,
        height: 60,
    };

    const centreLogo: ImageInfo = {
        bucket: 'locationdesjardins',
        folder: 'divers',
        itemId: 1,
        name: 'logo-entete-centre-du-travail.png',
        width: 128,
        height: 60,
    };

    const stihlLogo: ImageInfo = {
        bucket: 'locationdesjardins',
        folder: 'divers',
        itemId: 1,
        name: 'logo-entete-stihl.png',
        width: 139,
        height: 60,
    };

    return (
        <>
            <div className="hidden lg:flex bg-gradient-to-b from-primary to-primary-dark ">
                <div className="container-xl mx-auto w-full">
                    <div className="relative flex h-24 py-4 space-x-12 justify-end">
                        <Link href="/batteries-expert-plessisville">
                            <NextImage
                                alt="logo batteries-expert"
                                className="mx-auto block w-full h-full object-contain"
                                image={batterieLogo}
                            />
                        </Link>
                        <Link href="/centre-du-travail">
                            <NextImage
                                alt="logo centre-du-travail"
                                className="mx-auto block w-full h-full object-contain"
                                image={centreLogo}
                            />
                        </Link>
                        <Link href="/stihl-bois-francs">
                            <NextImage
                                alt="logo batteries-expert"
                                className="mx-auto block w-full h-full object-contain"
                                image={stihlLogo}
                            />
                        </Link>
                    </div>
                </div>
            </div>
            <Disclosure
                as="nav"
                className="bg-white w-full py-3 shadow border-b border-gray-100"
            >
                {({ open }) => (
                    <>
                        <div className="container-xl mx-auto">
                            <div className="relative flex h-20 justify-between">
                                <div className="absolute inset-y-0 left-0 flex items-center lg:hidden">
                                    <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md p-2 hover:border-primary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary">
                                        <span className="absolute -inset-0.5" />
                                        <span className="sr-only">
                                            Open main menu
                                        </span>
                                        {open ? (
                                            <HiXMark className="h-10 w-10 fill-gray-300 stroke-gray-600 border-gray-900 cursor-pointer" />
                                        ) : (
                                            <HiBars3 className="h-10 w-10 fill-gray-300 stroke-gray-600 border-gray-900 cursor-pointer" />
                                        )}
                                    </Disclosure.Button>
                                </div>
                                <div className="flex flex-1 items-center justify-end lg:justify-start">
                                    {imageInfo && (
                                        <div className="items-center h-full hidden 2xs:flex p-4 lg:p-0">
                                            <Link
                                                className="flex items-center h-full"
                                                href="/"
                                            >
                                                <NextImage
                                                    alt="logo"
                                                    className="mx-auto block w-full h-full object-contain"
                                                    image={imageInfo}
                                                />
                                            </Link>
                                        </div>
                                    )}
                                    <div className="hidden lg:w-full lg:h-full lg:ml-12 lg:flex lg:space-x-12 lg:justify-end">
                                        {pageLinks.map(
                                            (x: PageLink, i: number) => (
                                                <Link
                                                    className={`inline-flex items-center px-1 -mt-2 pt-1 text-2xl xl:text-3xl font-bold text-primary hover:text-primary-dark uppercase
                                            ${
                                                segment === `${x.link}`
                                                    ? 'text-primary lg:border-b-4 lg:-mt-3 lg:-mb-2 border-primary-light '
                                                    : 'lg:border-none'
                                            } `}
                                                    href={`/${x.link}`}
                                                    key={i}
                                                >
                                                    {x.title}
                                                </Link>
                                            ),
                                        )}
                                    </div>
                                    <SignOut locale={locale} />
                                </div>
                            </div>
                        </div>

                        <Disclosure.Panel className="lg:hidden">
                            <div className="space-y-1 pb-4 pt-2">
                                {pageLinks.map((x: PageLink, i: number) => (
                                    <Disclosure.Button
                                        as="a"
                                        className={`block bg-transparent py-2 pl-3 pr-4 text-2xl font-bold text-gray-700 uppercase
                                ${segment === `${x.link}` && 'text-primary'} `}
                                        href={`/${x.link}`}
                                        key={i}
                                    >
                                        {x.title}
                                    </Disclosure.Button>
                                ))}
                            </div>
                        </Disclosure.Panel>
                    </>
                )}
            </Disclosure>{' '}
        </>
    );
};
export default Navbar;
